import React, { useState } from "react";
import axios from "axios";
import swal from "sweetalert";

// RM CROWN MARKET — Asset Migration
// Drop-in replacement for the existing Checkout component.
// API, request payload, and business rules are preserved.
const Checkout = () => {
  const [form, setForm] = useState({ amount: "", password: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const displayError = (name) => {
    const error = errors?.[name];
    return Array.isArray(error) ? error.join(" ") : error || "";
  };

  const submitMigration = async (event) => {
    event.preventDefault();
    if (submitting || !accepted) return;
    if (!localStorage.getItem("auth_token")) {
      swal("Authentication Required", "Please log in before continuing.", "warning");
      return;
    }

    const amount = Number(form.amount);
    if (!form.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
      setErrors((prev) => ({ ...prev, amount: "Enter a valid amount greater than zero." }));
      return;
    }
    if (!form.password) {
      setErrors((prev) => ({ ...prev, password: "Account password is required." }));
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post("/sub-invest", {
        amount: form.amount,
        password: form.password,
        key_s: "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9",
      });

      if (data?.status === "success") {
        await swal("Success", data.message || "Migration request submitted successfully.", "success");
        setForm({ amount: "", password: "" });
        setErrors({});
        setAccepted(false);
      } else if (data?.status === "fail") {
        swal("Warning", data.message || "Migration request could not be completed.", "warning");
      } else {
        setErrors(data?.validation_error || {});
        if (data?.message && !data?.validation_error) {
          swal("Warning", data.message, "warning");
        }
      }
    } catch (error) {
      const serverErrors = error?.response?.data?.validation_error || error?.response?.data?.errors;
      if (serverErrors) setErrors(serverErrors);
      else swal("Error", error?.response?.data?.message || "Unable to submit migration. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        html, body, #root { background: #00191D !important; }
        body { color: #fff !important; }
        .rmam, .rmam * { box-sizing: border-box; }
        .rmam { --navy:#00191D; --cyan:#16B8C7; --muted:#a5bec3; --line:rgba(91,198,208,.22); color:#fff; background:#00191D; min-height:100vh; padding:18px 4px 40px; font-size:14px; line-height:1.55; }
        .rmam-shell { width:100%; max-width:1180px; margin:0 auto; }
        .rmam-hero { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:14px; padding:22px; border:1px solid var(--line); border-radius:22px; background:linear-gradient(115deg,#052e34,#00191D 72%); margin-bottom:16px; animation:rmam-rise .38s ease both; }
        .rmam-hero-title { display:flex; align-items:center; gap:14px; }
        .rmam-symbol { display:grid; place-items:center; flex:none; width:52px; height:52px; border-radius:16px; background:linear-gradient(135deg,#54e4e8,#16B8C7); color:#00252a; font-size:25px; font-weight:800; box-shadow:0 8px 25px rgba(22,184,199,.2); }
        .rmam h1, .rmam h2, .rmam h3, .rmam p { margin:0; }
        .rmam h1 { font-size:clamp(20px,3vw,27px); font-weight:800; letter-spacing:-.6px; color:#fff; }
        .rmam-sub { color:var(--muted); margin-top:4px !important; }
        .rmam-status { border:1px solid rgba(22,184,199,.32); border-radius:99px; padding:8px 12px; color:#78e9eb; background:rgba(22,184,199,.09); font-weight:700; font-size:12px; }
        .rmam-layout { display:grid; grid-template-columns:minmax(0,1.5fr) minmax(300px,1fr); gap:16px; align-items:start; }
        .rmam-card { min-width:0; border-radius:22px; border:1px solid var(--line); background:linear-gradient(160deg,#052a30,#012026 60%,#00191D); overflow:hidden; animation:rmam-rise .45s ease both; }
        .rmam-card-head { padding:20px 22px 17px; border-bottom:1px solid var(--line); display:flex; align-items:center; gap:11px; }
        .rmam-card-head h2 { font-size:17px; font-weight:800; color:#fff; }
        .rmam-card-head p { color:var(--muted); margin-top:3px; font-size:13px; }
        .rmam-step { display:grid; place-items:center; width:34px; height:34px; flex:none; border-radius:11px; background:rgba(22,184,199,.16); color:#6be4e9; font-weight:900; }
        .rmam-form { padding:22px; }
        .rmam-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
        .rmam-field { min-width:0; }
        .rmam-label { display:block; color:#fff; font-weight:700; margin-bottom:8px; }
        .rmam-input-wrap { position:relative; }
        .rmam-input { display:block; width:100%; height:52px; padding:0 14px; border-radius:13px; border:1px solid rgba(91,198,208,.3); outline:none; background:#fff; color:#112b30; font:inherit; font-size:14px; transition:border-color .18s,box-shadow .18s; }
        .rmam-input::placeholder { color:#74878b; }
        .rmam-input:focus { border-color:var(--cyan); box-shadow:0 0 0 3px rgba(22,184,199,.17); }
        .rmam-input-amount { padding-left:37px; }
        .rmam-prefix { position:absolute; top:50%; left:14px; transform:translateY(-50%); color:#147a85; font-weight:900; font-size:17px; }
        .rmam-input-password { padding-right:70px; }
        .rmam-eye { position:absolute; right:8px; top:50%; transform:translateY(-50%); border:0; border-radius:8px; padding:7px 8px; background:#eaf7f8; color:#176b75; font-size:12px; font-weight:800; cursor:pointer; }
        .rmam-error { display:block; color:#ff9a9e; margin-top:6px; font-size:12px; overflow-wrap:anywhere; }
        .rmam-note { display:flex; align-items:flex-start; gap:10px; margin-top:19px; padding:14px; border:1px solid rgba(22,184,199,.2); background:rgba(22,184,199,.06); border-radius:13px; color:#c7d9dd; }
        .rmam-note-icon { color:#63d9e2; font-size:18px; line-height:1; }
        .rmam-terms { display:flex; gap:11px; align-items:flex-start; margin:20px 0; cursor:pointer; color:#d9e8e9; }
        .rmam-terms input { margin-top:4px; width:17px; height:17px; accent-color:var(--cyan); flex:none; cursor:pointer; }
        .rmam-button { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; min-height:52px; border:0; border-radius:14px; background:linear-gradient(110deg,#46dce3,#16B8C7 55%,#078d9f); color:#00272d; font:inherit; font-size:14px; font-weight:900; cursor:pointer; box-shadow:0 12px 24px rgba(22,184,199,.15); transition:transform .18s,box-shadow .18s,opacity .18s; }
        .rmam-button:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 16px 26px rgba(22,184,199,.23); }
        .rmam-button:disabled { opacity:.55; cursor:not-allowed; }
        .rmam-footnote { text-align:center; color:var(--muted); font-size:12px; margin-top:12px !important; }
        .rmam-overview { padding:20px; }
        .rmam-amount-box { border:1px solid rgba(22,184,199,.24); border-radius:17px; padding:18px; background:rgba(22,184,199,.08); margin-bottom:16px; }
        .rmam-small { color:#b3cdd1; font-size:12px; font-weight:700; }
        .rmam-amount { color:#fff; font-size:clamp(25px,4vw,34px); font-weight:900; margin-top:5px; overflow-wrap:anywhere; font-variant-numeric:tabular-nums; }
        .rmam-amount strong { color:#54e0e4; }
        .rmam-rows { border:1px solid var(--line); border-radius:15px; overflow:hidden; }
        .rmam-row { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:14px; border-bottom:1px solid var(--line); }
        .rmam-row:last-child { border-bottom:0; }
        .rmam-row span { color:#b9d1d5; }
        .rmam-row strong { text-align:right; color:#fff; font-weight:800; }
        .rmam-row .rmam-cyan { color:#58dce3; }
        .rmam-warning { margin-top:16px; padding:14px; border-radius:14px; background:#fff; color:#17383d; border:1px solid #d8ebed; }
        .rmam-warning strong { display:block; margin-bottom:4px; color:#0b515d; }
        .rmam-warning p { color:#3d5e63; font-size:13px; }
        @keyframes rmam-rise { from { opacity:0; transform:translateY(9px); } to { opacity:1; transform:translateY(0); } }
        @media(max-width:800px) { .rmam-layout { grid-template-columns:1fr; } .rmam-overview-card { order:-1; } }
        @media(max-width:520px) { .rmam { padding-top:10px; } .rmam-hero { padding:15px; border-radius:16px; } .rmam-symbol { width:44px; height:44px; font-size:21px; } .rmam-card { border-radius:17px; } .rmam-card-head { padding:15px; } .rmam-form,.rmam-overview { padding:15px; } .rmam-grid { grid-template-columns:1fr; gap:15px; } .rmam-row { padding:12px; } }
        @media(prefers-reduced-motion:reduce) { .rmam *, .rmam { animation:none !important; transition:none !important; } }
      `}</style>

      <main className="rmam">
        <div className="rmam-shell">
          <header className="rmam-hero">
            <div className="rmam-hero-title">
              <span className="rmam-symbol" aria-hidden="true">↗</span>
              <div>
                <h1>Asset Migration</h1>
                <p className="rmam-sub">Transfer your migration balance to your principal wallet.</p>
              </div>
            </div>
            <span className="rmam-status">Principal Wallet Migration</span>
          </header>

          <div className="rmam-layout">
            <section className="rmam-card" aria-labelledby="rmam-form-title">
              <div className="rmam-card-head">
                <span className="rmam-step">01</span>
                <div><h2 id="rmam-form-title">Migration Request</h2><p>Enter your amount and confirm with your account password.</p></div>
              </div>
              <form className="rmam-form" onSubmit={submitMigration}>
                <div className="rmam-grid">
                  <div className="rmam-field">
                    <label className="rmam-label" htmlFor="rmam-amount">Migration Amount</label>
                    <div className="rmam-input-wrap">
                      <span className="rmam-prefix" aria-hidden="true">$</span>
                      <input id="rmam-amount" className="rmam-input rmam-input-amount" type="number" name="amount" min="0.01" step="any" inputMode="decimal" placeholder="Enter amount" value={form.amount} onChange={onChange} required aria-invalid={!!displayError("amount")} />
                    </div>
                    {displayError("amount") && <span className="rmam-error" role="alert">{displayError("amount")}</span>}
                  </div>
                  <div className="rmam-field">
                    <label className="rmam-label" htmlFor="rmam-password">Account Password</label>
                    <div className="rmam-input-wrap">
                      <input id="rmam-password" className="rmam-input rmam-input-password" type={showPassword ? "text" : "password"} name="password" placeholder="Enter account password" autoComplete="current-password" value={form.password} onChange={onChange} required aria-invalid={!!displayError("password")} />
                      <button type="button" className="rmam-eye" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? "Hide" : "Show"}</button>
                    </div>
                    {displayError("password") && <span className="rmam-error" role="alert">{displayError("password")}</span>}
                  </div>
                </div>

                <div className="rmam-note"><span className="rmam-note-icon" aria-hidden="true">ⓘ</span><span>Please review the migration details before submitting. The request is processed according to your account's migration rules.</span></div>

                <label className="rmam-terms" htmlFor="rmam-terms">
                  <input id="rmam-terms" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} required />
                  <span>I have reviewed the migration information and accept the Terms &amp; Conditions.</span>
                </label>

                <button className="rmam-button" type="submit" disabled={submitting || !accepted}>
                  {submitting ? "Submitting Request..." : "Continue Asset Migration →"}
                </button>
                <p className="rmam-footnote">Your account password is required to authorize this request.</p>
              </form>
            </section>

            <aside className="rmam-card rmam-overview-card" aria-labelledby="rmam-overview-title">
              <div className="rmam-card-head">
                <span className="rmam-step">02</span>
                <div><h2 id="rmam-overview-title">Migration Overview</h2><p>Review your request at a glance.</p></div>
              </div>
              <div className="rmam-overview">
                <div className="rmam-amount-box"><div className="rmam-small">MIGRATION PAYMENT</div><div className="rmam-amount"><strong>$</strong>{form.amount || "0.00"}</div></div>
                <div className="rmam-rows">
                  <div className="rmam-row"><span>Migration Type</span><strong>Asset Migration</strong></div>
                  <div className="rmam-row"><span>Destination</span><strong>Principal Wallet</strong></div>
                  <div className="rmam-row"><span>Migration Rate</span><strong className="rmam-cyan">100%</strong></div>
                  <div className="rmam-row"><span>Migration Payment</span><strong>${form.amount || "0.00"}</strong></div>
                </div>
                <div className="rmam-warning"><strong>Important: 29-Day Restriction</strong><p>The migrated balance will not be returned before 29 days, according to the existing migration terms.</p></div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
};

export default Checkout;
